import { useEffect, useState } from "react";
import { Loader2, Radio, Globe, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { ContentCard } from "@/components/ContentCard";
import { useAuth } from "@/context/AuthContext";

export const flag = (cc) =>
  cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "🌍";
export const regionName = (cc) => {
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(cc) || cc; } catch { return cc; }
};

export default function LiveTV() {
  const { t } = useTranslation();
  const [cats, setCats] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [active, setActive] = useState("");
  const [country, setCountry] = useState("");
  const [language, setLanguage] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { kids } = useAuth();

  useEffect(() => {
    api.get("/channels/categories").then(({ data }) => setCats(data));
    api.get("/channels/countries").then(({ data }) => setCountries(data.slice(0, 16)));
    api.get("/channels/languages").then(({ data }) => setLanguages(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/channels", { params: { category: active, country, language, kids, limit: 80 } })
      .then(({ data }) => setItems(data.items)).finally(() => setLoading(false));
  }, [active, country, language, kids]);

  return (
    <div className="pt-24 px-4 sm:px-8 lg:px-12 pb-20 min-h-screen" data-testid="live-tv-page">
      <div className="flex items-center gap-3 mb-2">
        <Radio className="text-brand" size={28} />
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">{t("live.title")}</h1>
      </div>
      <p className="text-zinc-400 mb-6">{t("live.subtitle")}</p>

      <div className="flex items-center gap-2 mb-2 mt-2">
        <Languages size={16} className="text-zinc-400" />
        <span className="text-sm text-zinc-400">{t("common.browseByLanguage")}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-3 mb-2" data-testid="language-filters">
        <button onClick={() => setLanguage("")} data-testid="lang-all"
          className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${language === "" ? "bg-white text-black" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
          🌐 {t("common.allLanguages")}
        </button>
        {languages.map((l) => (
          <button key={l.value} onClick={() => setLanguage(l.value)} data-testid={`lang-${l.value}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${language === l.value ? "bg-white text-black" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
            {l.label}<span className="text-xs opacity-60">{l.count}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-2 mt-2">
        <Globe size={16} className="text-zinc-400" />
        <span className="text-sm text-zinc-400">{t("common.browseByCountry")}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-3 mb-2" data-testid="country-filters">
        <button onClick={() => setCountry("")} data-testid="country-all"
          className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${country === "" ? "bg-white text-black" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
          🌍 {t("common.all")}
        </button>
        {countries.map((c) => (
          <button key={c.code} onClick={() => setCountry(c.code)} data-testid={`country-${c.code}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${country === c.code ? "bg-white text-black" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
            <span className="text-base leading-none">{flag(c.code)}</span> {regionName(c.code)}
            <span className="text-xs opacity-60">{c.count}</span>
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-4 mb-4" data-testid="category-filters">
        <button onClick={() => setActive("")} data-testid="cat-all"
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${active === "" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
          {t("common.allChannels")}
        </button>
        {cats.map((c) => (
          <button key={c.value} onClick={() => setActive(c.value)} data-testid={`cat-${c.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${active === c.value ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" size={36} /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((it) => <ContentCard key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
}
